import { useCallback, useState } from "react";

import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Token } from "@stripe/stripe-js";
import { useIntl } from "react-intl";
import invariant from "tiny-invariant";

const useCardInput = () => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | undefined>(undefined);
  const [loadingToken, setLoadingToken] = useState(false);

  const getToken = useCallback(async (): Promise<Token | undefined> => {
    if (!stripe || !elements) {
      return;
    }

    setError(undefined);
    setLoadingToken(true);

    try {
      const cardElement = elements.getElement(CardElement);
      invariant(!!cardElement, "CardElement not found");

      const { error, token } = await stripe.createToken(cardElement, {});
      if (error) {
        setError(error.message);
        setLoadingToken(false);
        return;
      }
      setLoadingToken(false);
      invariant(!!token, "Token not defined");

      return token;
    } catch {
      setError(
        intl.formatMessage({
          id: "billing.checkout.token.failed",
          defaultMessage: "Failed to upgrade plan: Unknown Stripe error",
          description: "Error message when failed to get stripe token",
        })
      );
      setLoadingToken(false);
    }
  }, [elements, intl, setError, setLoadingToken, stripe]);

  return { error, getToken, loadingToken };
};

export default useCardInput;
